import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ClaimStatus, RequestType } from '../types';
import type {
  ClaimStore,
  Claim,
  ValidationResult,
  WorkflowState
} from '../types/interfaces';
import {
  createClaimOnExistingEvent,
  saveClaimProgress,
  getClaim,
  getCurrentUserDrafts,
  getClaimList,
  submitClaim as apiSubmitClaim,
  cancelClaim as apiCancelClaim,
  type ClaimListResponse
} from '../services/api';
import { autoMigrateStepData, getScreenCodeForStep } from '../utils/screenCodeMapper';
import { calculateProgressPercentage } from '../utils/flowScreens';
import { cleanStepDataForFlowChange, isFlowChangeDetected, getScreensToBeRemoved } from '../utils/flowDataCleanup';
import { dropDataReusedAcrossClaims } from '../utils/eventDateChange';
import { withoutEligibilityLoading } from '../utils/eligibilityStepData';
import type { FlowType } from '../config/flowDefinitions';
import { logger } from '../utils/logger';
import { claimNotEditableCodeOf, type ClaimNotEditableCode } from '../utils/claimNotEditable';

interface ClaimState {
  // Current claim data
  currentClaim: Claim | null;
  isLoading: boolean;
  error: string | null;

  // BFF integration fields
  eventId: string | null; // Salesforce Event__c ID
  eventNumber: string | null; // Auto-generated event number
  submissionId: string | null; // Portal_Submission__c ID (null for legacy claims)
  referenceNumber: number | null; // Generated at event creation - shared across all documents
  existingEventNumber: string | null; // When linking to an existing event

  // Draft management
  drafts: { [id: string]: Partial<Claim> };
  draftsList: any[]; // UI-formatted list of drafts from API
  sentClaimsList: any[]; // UI-formatted list of sent claims from API
  lastSaved: Date | null;

  // Form data for each screen (migrated from step numbers to screen codes)
  stepData: { [screenCode: string]: any };

  // Parent context data (from embedded app) - stored separately from stepData
  parentContext: {
    contracts?: any[];
    clientId?: string;
    clientInfo?: any;
  } | null;

  // Progress tracking (screen code based)
  currentScreenCode: string | null;

  // Deferred claim creation - wait until after 01-01-CONTRAT
  deferClaimCreation: boolean;

  // RTA status
  rtaEligible: boolean;
  eligibilityResult: any;

  // Reference claim — a previously-submitted claim loaded in read-only mode to support flows
  // that act on an existing claim (e.g., "append expenses"). Kept separate from currentClaim
  // so the wizard's own state is not polluted by the reference data. Intentionally generic so
  // future "act on existing claim" flows can reuse it.
  referenceClaim: import('../types').ReferenceClaim | null;

  // DEV-8836 — set when the BFF refuses a save because this draft was abandoned/deleted elsewhere
  // (another tab or window). While set, saveDraft is a no-op and ClaimNotEditableModal is shown.
  claimNotEditable: ClaimNotEditableCode | null;
}

const initialState: ClaimState = {
  currentClaim: null,
  isLoading: false,
  error: null,
  eventId: null,
  eventNumber: null,
  submissionId: null,
  referenceNumber: null,
  existingEventNumber: null,
  drafts: {},
  draftsList: [],
  sentClaimsList: [],
  lastSaved: null,
  stepData: {},
  parentContext: null,
  currentScreenCode: null,
  deferClaimCreation: false,
  rtaEligible: false,
  eligibilityResult: null,
  referenceClaim: null,
  claimNotEditable: null
};

export const useClaimStore = create<ClaimStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Initialize a new claim
        initializeClaim: (type: RequestType, data?: any) => {
          const claimId = crypto.randomUUID();

          // Preserve existing stepData (e.g., from ContactPreferences saved before claim creation)
          const existingStepData = get().stepData || {};

          const newClaim: Claim = {
            id: claimId,
            contractNumber: data?.contractNumber || '',
            status: ClaimStatus.DRAFT,
            totalAmountCAD: 0,
            rtaEligible: false,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
            version: 1,
            beneficiaries: [],
            expenses: [],
            documents: [],
            medicalEvents: [],
            workflow: {
              currentScreenCode: null,
              completedScreenCodes: [],
              stepData: existingStepData // Include existing stepData in workflow
            },
            createdAt: new Date(),
            updatedAt: new Date()
          };

          // Initialize with screen code
          const initialScreenCode = '00-02-AVIS'; // Legal notice screen

          // Merge existing stepData with initial screen data
          const mergedStepData = {
            ...existingStepData,
            [initialScreenCode]: {
              ...existingStepData[initialScreenCode],
              type,
              ...data
            }
          };

          set({
            currentClaim: newClaim,
            currentScreenCode: initialScreenCode,
            stepData: mergedStepData, // Use merged stepData instead of replacing
            eventId: null,  // Reset eventId to ensure POST request
            eventNumber: null,  // Reset event number
            submissionId: null,  // Reset submission ID
            referenceNumber: null,  // Reset reference number
            rtaEligible: false,  // Reset RTA eligibility
            eligibilityResult: null,  // Reset eligibility result
            referenceClaim: null,  // Clear any lingering append-flow reference
            claimNotEditable: null,  // A new claim is editable again (DEV-8836)
            error: null
          }, false, 'initializeClaim');
        },

        // Update claim data for a specific screen (using screen code)
        updateClaimData: (data: any, screenCode?: string) => {
          const { currentScreenCode, currentClaim } = get();

          // Use explicit screenCode or fallback to currentScreenCode
          const code = screenCode || currentScreenCode;

          if (!code) {
            return;
          }

          // ALWAYS update stepData even if no claim exists yet
          // This allows early screens (like ContactPreferences) to save data before claim initialization
          const updatedStepData = {
            ...get().stepData,
            [code]: { ...get().stepData[code], ...data }
          };

          // Only update currentClaim if it exists
          if (currentClaim) {
            const updatedClaim = {
              ...currentClaim,
              updatedAt: new Date(),
              workflow: {
                ...currentClaim.workflow,
                stepData: updatedStepData
              }
            };

            set({
              currentClaim: updatedClaim,
              stepData: updatedStepData,
              lastSaved: new Date()
            }, false, 'updateClaimData');
          } else {
            // No claim yet, but still save stepData for later use
            set({
              stepData: updatedStepData,
              lastSaved: new Date()
            }, false, 'updateClaimData-beforeClaimInit');
          }
        },

        // Drop a single screen's answers from stepData (DEV-7479).
        // Needed when a conditional screen becomes unreachable after the user went
        // back and changed the answer that gates it: without this purge the stale
        // answers would still be submitted to NOVA.
        clearScreenData: (screenCode: string) => {
          const { stepData, currentClaim } = get();

          if (!(screenCode in stepData)) {
            return;
          }

          const remainingStepData = Object.fromEntries(
            Object.entries(stepData).filter(([code]) => code !== screenCode)
          );

          if (currentClaim) {
            set({
              currentClaim: {
                ...currentClaim,
                updatedAt: new Date(),
                workflow: {
                  ...currentClaim.workflow,
                  stepData: remainingStepData
                }
              },
              stepData: remainingStepData,
              lastSaved: new Date()
            }, false, 'clearScreenData');
          } else {
            set({
              stepData: remainingStepData,
              lastSaved: new Date()
            }, false, 'clearScreenData-beforeClaimInit');
          }
        },

        // Drop SOME keys of a single screen, keeping everything else (DEV-7682 ST7).
        //
        // `clearScreenData` empties a WHOLE screen and `updateClaimData` merges — neither
        // can REMOVE one key. Confirming an event-date change needs exactly that on
        // `01-01-CONTRAT`: the eligibility verdict computed at the OLD date has to go,
        // while the contract itself (`selectedContract`, `contractNumber`,
        // `contractDetails` and its Parent Context trips) stays — that is what the warning
        // modal promises to keep.
        //
        // The caller owns WHICH keys (`ELIGIBILITY_DERIVED_CONTRACT_KEYS` in
        // `utils/eventDateChange.ts`); this action is deliberately generic so no domain
        // list ever gets copied into the store.
        clearScreenDataKeys: (screenCode: string, keys: readonly string[]) => {
          const { stepData, currentClaim } = get();
          const screenData = stepData[screenCode];

          // Absent, primitive or array screen data has no named keys to drop.
          if (!screenData || typeof screenData !== 'object' || Array.isArray(screenData)) {
            return;
          }

          const remainingEntries = Object.entries(screenData).filter(
            ([key]) => !keys.includes(key)
          );

          // Nothing matched — bail out rather than emit a new object and re-render every
          // subscriber of this screen for no change.
          if (remainingEntries.length === Object.keys(screenData).length) {
            return;
          }

          const updatedStepData = {
            ...stepData,
            [screenCode]: Object.fromEntries(remainingEntries)
          };

          if (currentClaim) {
            set({
              currentClaim: {
                ...currentClaim,
                updatedAt: new Date(),
                workflow: {
                  ...currentClaim.workflow,
                  stepData: updatedStepData
                }
              },
              stepData: updatedStepData,
              lastSaved: new Date()
            }, false, 'clearScreenDataKeys');
          } else {
            set({
              stepData: updatedStepData,
              lastSaved: new Date()
            }, false, 'clearScreenDataKeys-beforeClaimInit');
          }
        },

        // Complete a screen (mark as completed)
        // Note: This is now a no-op since we track completion via stepData
        // Kept for backward compatibility with existing code
        completeScreen: (screenCode: string) => {
          // No-op: Completion is tracked via stepData presence
          // Auto-save is handled by useAutoSave hook (watches stepData changes)
        },

        // Clean stepData when changing flow (e.g., switching from MEDICAL to CANCELLATION)
        cleanStepDataForFlow: (newFlowType: FlowType) => {
          const { stepData, currentClaim } = get();


          // Get screens that will be removed
          const screensToRemove = getScreensToBeRemoved(stepData, newFlowType);


          // Clean the stepData
          const cleanedStepData = cleanStepDataForFlowChange(stepData, newFlowType);

          // Update store with cleaned data
          if (currentClaim) {
            const updatedClaim = {
              ...currentClaim,
              updatedAt: new Date(),
              workflow: {
                ...currentClaim.workflow,
                stepData: cleanedStepData
              }
            };

            set({
              currentClaim: updatedClaim,
              stepData: cleanedStepData,
              lastSaved: new Date()
            }, false, 'cleanStepDataForFlow');
          } else {
            // No claim yet, just clean stepData
            set({
              stepData: cleanedStepData,
              lastSaved: new Date()
            }, false, 'cleanStepDataForFlow-beforeClaimInit');
          }

          return screensToRemove;
        },

        // Check if changing flow would cause data loss
        checkFlowChange: (newFlowType: FlowType): { willLoseData: boolean; screensAffected: string[] } => {
          const { stepData } = get();
          const willLoseData = isFlowChangeDetected(stepData, newFlowType);
          const screensAffected = willLoseData ? getScreensToBeRemoved(stepData, newFlowType) : [];

          return { willLoseData, screensAffected };
        },

        // Save current state as draft
        saveDraft: async () => {
          const { currentClaim, stepData, eventId, currentScreenCode, referenceClaim } = get();

          if (!currentClaim) return;

          // Append-expenses flow (DEV-6623) never creates a draft Submission — any save is a bug.
          // Silent no-op is safer than throwing because shared screens (e.g., InvoiceUploadScreen)
          // call saveDraft() on their own lifecycle and we don't want to break them in other flows.
          if (referenceClaim) {
            return;
          }

          // DEV-8836: this draft was abandoned/deleted elsewhere — every further save would be
          // refused the same way. Stop here; ClaimNotEditableModal sends the user home.
          if (get().claimNotEditable) {
            return;
          }

          // ADR-061 (Deferred Claim Creation) — SAFETY NET for any caller that bypasses useAutoSave.
          // No claim-creation POST may be emitted before the contract is selected. The very first
          // POST (eventId == null) creates the Event__c; the BFF rejects it when contractNumber is
          // empty ("Contract number is required to create a claim"). Resolve the prospective
          // contractNumber with the SAME chain used below, then refuse the first POST when either:
          //   - the flow explicitly deferred creation (deferClaimCreation), OR
          //   - no contract number is resolvable yet (defends even if the flag wiring is missed).
          // Once eventId exists we are updating an already-created claim, so do NOT change behavior.
          // The `eventId == null` term preserves the existing-event flow (createNewClaimOnExistingEvent sets
          // eventId) and works alongside the referenceClaim guard for append-expenses (DEV-6623).
          if (eventId == null) {
            const prospectiveContractData = stepData['01-01-CONTRAT'] || {};
            const prospectiveContractNumber = prospectiveContractData.contractDetails?.number ||
                                               prospectiveContractData.contractDetails?.contractNumber ||
                                               prospectiveContractData.selectedContract ||
                                               currentClaim.contractNumber || '';
            if (get().deferClaimCreation || !prospectiveContractNumber) {
              return;
            }
          }

          set({ isLoading: true }, false, 'saveDraft:start');

          try {
            // Calculate progress percentage based on current screen position
            const flowType = get().getCurrentClaimType();
            const progressPercentage = calculateProgressPercentage(currentScreenCode, flowType);

            // Extract essential data from stepData (contract selection - step 01-01-CONTRAT)
            const contractData = stepData['01-01-CONTRAT'] || {};
            const contractNumber = contractData.contractDetails?.number ||
                                   contractData.contractDetails?.contractNumber ||
                                   contractData.selectedContract ||
                                   currentClaim.contractNumber || '';

            // Calculate total amount from expense validation screen (04-02-VALIDATION)
            const expenseValidationData = stepData['04-02-VALIDATION'] || {};
            const totalAmountCAD = expenseValidationData.totalAmountCAD || currentClaim.totalAmountCAD || 0;

            // Generate or retrieve referenceNumber
            // On first save (eventId is null), generate new referenceNumber
            // On subsequent saves, use existing referenceNumber from state
            let referenceNumber = get().referenceNumber;
            if (!referenceNumber && !eventId) {
              // First save - generate new referenceNumber
              referenceNumber = Date.now();
            }

            // Build claim data - only send what the backend needs
            // The workflow.stepData contains all form data collected from the user
            const claimData = {
              workflow: {
                currentScreenCode,
                // DEV-8026: the legacy in-flight flag never goes back to NOVA.
                stepData: withoutEligibilityLoading(stepData),
                // Preserve _salesforceMetadata if it exists (for UPDATE operations)
                ...(currentClaim.workflow?._salesforceMetadata && {
                  _salesforceMetadata: currentClaim.workflow._salesforceMetadata
                })
              },
              // Include essential claim metadata extracted from stepData
              // NOTE (DEV-7699): no policyholderID here. It used to mirror the parent
              // context's clientId, which identifies the CONNECTED USER, not the policy
              // holder — a wrong mapping, persisted verbatim into Portal_Data__c and read
              // back on resume. Nothing consumed it. clientId now stays a parent-context
              // value used only for Datadog attribution (see utils/loggingContext.ts).
              contractNumber,
              totalAmountCAD,
              rtaEligible: currentClaim.rtaEligible,
              // CRITICAL: Include referenceNumber in every save so it persists to Salesforce Portal_Data__c
              referenceNumber: referenceNumber,
            };

            // Call BFF API to save progress
            const response = await saveClaimProgress(
              claimData,
              progressPercentage,
              currentScreenCode || '00-02-AVIS',
              get().getCurrentClaimType() || undefined,
              eventId || undefined,
              totalAmountCAD > 0 ? totalAmountCAD : undefined // Pass portalApprovedAmount if available
            );

            // Update local state with response
            set({
              eventId: response.eventId,
              eventNumber: response.eventNumber || null,
              submissionId: response.submissionId || get().submissionId || null,
              // Use referenceNumber from response if available, otherwise use the one we generated
              referenceNumber: response.referenceNumber || referenceNumber || null,
              lastSaved: new Date(),
              isLoading: false,
              error: null,
            }, false, 'saveDraft:success');


          } catch (error: any) {
            const notEditable = claimNotEditableCodeOf(error);
            if (notEditable) {
              // An ordinary user action in another tab, not a defect: warn, not error.
              logger.warn('Draft no longer editable', {
                errorType: 'ClaimNotEditable',
                store: 'ClaimStore',
                reason: notEditable
              });
              set({
                isLoading: false,
                error: null,
                claimNotEditable: notEditable
              }, false, 'saveDraft:notEditable');
              return;
            }
            logger.error('Failed to save draft', {
              errorType: 'SaveDraftError',
              store: 'ClaimStore'
            }, error);
            set({
              isLoading: false,
              error: error.message || 'Failed to save draft'
            }, false, 'saveDraft:error');
          }
        },

        // Load a saved draft (or sent claim)
        loadDraft: async (eventId: string, submissionId?: string) => {
          set({ isLoading: true }, false, 'loadDraft:start');

          try {
            // Call BFF API to get claim by ID (pass submissionId for dual-state disambiguation)
            const response = await getClaim(eventId, submissionId);

            // Extract claim data from response
            const claimData = response.claimData || {};
            const workflow = claimData.workflow || {};

            // Get currentScreenCode from top-level response (not from workflow)
            const currentScreenCode = response.currentScreenCode || workflow.currentScreenCode || null;

            // Auto-migrate legacy step-based data to screen codes
            // DEV-8026: the legacy in-flight flag dies here, on the first resume, so nothing
            // downstream can spread it back into the claim data.
            const migratedStepData = withoutEligibilityLoading(autoMigrateStepData(workflow.stepData || {}));

            // Reconstruct claim object
            const claim: Claim = {
              id: response.eventId,
              contractNumber: claimData.contractNumber || '',
              // DEV-7699: policyholderID is deliberately NOT read back from NOVA. Legacy
              // claims still carry the key in Portal_Data__c; it is ignored on resume so
              // the value can never be re-fed from anywhere but the parent context.
              status: response.status as any,
              totalAmountCAD: claimData.totalAmountCAD || 0,
              rtaEligible: claimData.rtaEligible || false,
              expiresAt: claimData.expiresAt ? new Date(claimData.expiresAt) : new Date(),
              version: claimData.version || 1,
              beneficiaries: claimData.beneficiaries || [],
              expenses: claimData.expenses || [],
              documents: claimData.documents || [],
              medicalEvents: claimData.medicalEvents || [],
              workflow: {
                currentScreenCode: currentScreenCode,
                stepData: migratedStepData,
                // Preserve _salesforceMetadata from backend response
                ...(workflow._salesforceMetadata && {
                  _salesforceMetadata: workflow._salesforceMetadata
                })
              },
              createdAt: response.createdAt ? new Date(response.createdAt) : new Date(),
              updatedAt: response.updatedAt ? new Date(response.updatedAt) : new Date(),
            };


            set({
              currentClaim: claim,
              eventId: response.eventId,
              eventNumber: response.eventNumber || null,
              submissionId: response.submissionId || null,
              // Extract from response OR from claimData for backward compatibility
              referenceNumber: response.referenceNumber || claimData.referenceNumber || null,
              stepData: migratedStepData,
              currentScreenCode: currentScreenCode,
              isLoading: false,
              error: null,
              claimNotEditable: null, // a freshly loaded draft is editable (DEV-8836)
              // Clear any lingering append-flow reference — loading a regular claim means
              // we're back in draft/edit mode and auto-save must resume.
              referenceClaim: null
            }, false, 'loadDraft:success');


          } catch (error: any) {
            logger.error('Failed to load draft', {
              errorType: 'LoadDraftError',
              store: 'ClaimStore'
            }, error);
            set({
              isLoading: false,
              error: error.message || 'Failed to load draft'
            }, false, 'loadDraft:error');
          }
        },

        // Submit the final claim
        submitClaim: async () => {
          const { currentClaim, eventId, stepData, currentScreenCode } = get();

          if (!currentClaim || !eventId) {
            set({ error: 'Cannot submit claim: No claim data or event ID' }, false, 'submitClaim:error');
            return;
          }

          set({ isLoading: true }, false, 'submitClaim:start');

          try {
            // Save final state with 100% progress before submitting
            const flowType = get().getCurrentClaimType();

            // Extract essential data from stepData
            const contractData = stepData['01-01-CONTRAT'] || {};
            const contractNumber = contractData.contractDetails?.number ||
                                   contractData.contractDetails?.contractNumber ||
                                   contractData.selectedContract ||
                                   currentClaim.contractNumber || '';

            const expensesData = stepData['04-01-FACTURES'] || {};
            const totalAmountCAD = expensesData.totalAmount || currentClaim.totalAmountCAD || 0;

            const claimData = {
              workflow: {
                currentScreenCode,
                // DEV-8026: the legacy in-flight flag never goes back to NOVA.
                stepData: withoutEligibilityLoading(stepData),
                // Preserve _salesforceMetadata if it exists (for UPDATE operations)
                ...(currentClaim.workflow?._salesforceMetadata && {
                  _salesforceMetadata: currentClaim.workflow._salesforceMetadata
                })
              },
              // No policyholderID — see the DEV-7699 note on the saveDraft payload
              contractNumber,
              totalAmountCAD,
              rtaEligible: currentClaim.rtaEligible,
              // CRITICAL: Include referenceNumber so it's available for PDF generation
              referenceNumber: get().referenceNumber,
            };


            // Save with 100% progress before submitting
            await saveClaimProgress(
              claimData,
              100, // Final progress is always 100%
              currentScreenCode || '05-03-FIN',
              flowType || undefined,
              eventId
            );

            // Call BFF API to submit claim
            const response = await apiSubmitClaim(eventId);

            // Update local state with submission response
            const finalClaim = {
              ...currentClaim,
              caseNumber: response.caseNumber || `CL-${Date.now().toString().slice(-6)}`,
              status: response.status as any,
              submittedAt: new Date(),
              rtaEligible: response.claimData?.rtaEligible || false,
            };

            set({
              currentClaim: finalClaim,
              rtaEligible: response.claimData?.rtaEligible || false,
              isLoading: false,
              error: null
            }, false, 'submitClaim:success');

          } catch (error: any) {
            const notEditable = claimNotEditableCodeOf(error);
            if (notEditable) {
              // DEV-8836: abandoned/deleted elsewhere — ClaimNotEditableModal takes over.
              logger.warn('Draft no longer editable', {
                errorType: 'ClaimNotEditable',
                store: 'ClaimStore',
                reason: notEditable
              });
              set({
                isLoading: false,
                error: null,
                claimNotEditable: notEditable
              }, false, 'submitClaim:notEditable');
              throw error; // still a failed submit: ConfirmationScreen must not report success
            }
            logger.error('Failed to submit claim', {
              errorType: 'SubmitClaimError',
              store: 'ClaimStore'
            }, error);
            set({
              isLoading: false,
              error: error.message || 'Failed to submit claim'
            }, false, 'submitClaim:error');
            throw error; // Re-throw so ConfirmationScreen's catch can show the error modal and clear claimSubmitted for retry
          }
        },

        // Cancel the current claim (abandon draft)
        cancelClaim: async () => {
          const { currentClaim, eventId } = get();

          if (!currentClaim || !eventId) {
            set({ error: 'Cannot cancel claim: No claim data or event ID' }, false, 'cancelClaim:error');
            return;
          }

          set({ isLoading: true }, false, 'cancelClaim:start');

          try {
            // Call BFF API to cancel claim (sets status to CANCELLED)
            const response = await apiCancelClaim(eventId);

            // Update local state with cancellation response
            const cancelledClaim = {
              ...currentClaim,
              status: response.status as any,
              updatedAt: new Date(),
            };

            set({
              currentClaim: cancelledClaim,
              isLoading: false,
              error: null
            }, false, 'cancelClaim:success');

            // Clear the claim from state after successful cancellation
            set(initialState, false, 'cancelClaim:clear');

          } catch (error: any) {
            logger.error('Failed to cancel claim', {
              errorType: 'CancelClaimError',
              store: 'ClaimStore'
            }, error);
            set({
              isLoading: false,
              error: error.message || 'Failed to cancel claim'
            }, false, 'cancelClaim:error');
            throw error; // Re-throw so UI can handle error
          }
        },

        // Clear current claim and reset state
        clearClaim: () => {
          set(initialState, false, 'clearClaim');
        },

        // Clear claim data from a specific section onwards (for "Submit Another" flow)
        // Keeps sections 00 (Entry) and 01 (Contract/Contact) but clears 02+ (Claim Type, Details, Documents)
        //
        // ADR-050 (amended by DEV-7682): the set of answers reused from one claim of the
        // session to the next is now `travel dates + contract` — it used to include the
        // beneficiary AND the event date. Two claims of the same trip are two distinct
        // events, so the next claim asks its own date and re-queries eligibility at that
        // date; the people covered are whatever THAT answer returns, never the previous
        // claim's list. `dropDataReusedAcrossClaims` owns which keys that removes.
        clearClaimFromSection: (sectionPrefix: string) => {
          const { stepData, currentClaim } = get();

          // Filter stepData to keep only screens that DON'T start with the section prefix or higher
          const keptSectionsStepData = Object.keys(stepData)
            .filter(screenCode => {
              const section = screenCode.split('-')[0]; // Extract section number (e.g., "02" from "02-00-GARANTIE")
              return parseInt(section) < parseInt(sectionPrefix);
            })
            .reduce((acc, screenCode) => {
              acc[screenCode] = stepData[screenCode];
              return acc;
            }, {} as Record<string, any>);

          // …then drop, out of those kept sections, what a new claim must NOT inherit:
          // the event date and the eligibility verdict computed at that date (the
          // beneficiaries the next claim would otherwise be prefilled with).
          const filteredStepData = dropDataReusedAcrossClaims(keptSectionsStepData);


          // Extract preserved data from kept sections
          const contractData = filteredStepData['01-01-CONTRAT'] || {};
          const contactData = filteredStepData['01-00-DEMANDEUR'] || {};

          // Create a minimal new claim with preserved basic info
          const newClaim: Claim = {
            id: crypto.randomUUID(), // New local ID
            contractNumber: contractData.contractDetails?.number ||
                           contractData.contractDetails?.contractNumber ||
                           contractData.selectedContract ||
                           currentClaim?.contractNumber || '',
            status: ClaimStatus.DRAFT,
            totalAmountCAD: 0,
            rtaEligible: false,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
            version: 1,
            beneficiaries: [],
            expenses: [],
            documents: [],
            medicalEvents: [],
            workflow: {
              currentScreenCode: null,
              completedScreenCodes: Object.keys(filteredStepData),
              stepData: filteredStepData
            },
            createdAt: new Date(),
            updatedAt: new Date()
          };


          set({
            stepData: filteredStepData,
            eventId: null, // Reset eventId so next save creates NEW claim (POST)
            eventNumber: null,
            referenceNumber: null, // Reset reference number
            currentClaim: newClaim, // ✅ Keep a claim object so AutoSave works
            currentScreenCode: null,
            rtaEligible: false,
            eligibilityResult: null,
          }, false, 'clearClaimFromSection');
        },

        // Set error message
        setError: (error: string | null) => {
          set({ error }, false, 'setError');
        },

        // Set loading state
        setLoading: (isLoading: boolean) => {
          set({ isLoading }, false, 'setLoading');
        },

        // Get available drafts (synchronous - returns from state)
        getDrafts: () => {
          return get().draftsList;
        },

        // Fetch drafts from API (async)
        fetchDrafts: async () => {
          set({ isLoading: true }, false, 'fetchDrafts:start');

          try {
            // Call BFF API to get drafts for current user
            const drafts = await getCurrentUserDrafts();

            // Transform API response to UI format
            const uiDrafts = drafts.filter(draft => draft != null).map((draft) => {
              const claimData = draft.claimData || {};
              const workflow = claimData.workflow || {};
              const stepData = workflow.stepData || {};

              // Extract claim type from workflow data (02-00-GARANTIE step)
              // Keep the raw value (medical, cancellation, baggage) for translation in UI
              const selectedType = stepData['02-00-GARANTIE']?.selectedType;
              let claimType = selectedType || draft.flowType || null;
              // Ensure lowercase and validate claim type
              if (claimType) {
                claimType = claimType.toLowerCase();
                // Only keep valid claim types, otherwise set to null
                const validTypes = ['medical', 'cancellation', 'baggage'];
                if (!validTypes.includes(claimType)) {
                  claimType = null;
                }
              }

              // Extract beneficiary from stepData
              const beneficiaryData = stepData['02M-01-BENEFICIAIRE'] || stepData['02A-01-BENEFICIAIRE'] || stepData['02B-01-BENEFICIAIRE'] || {};
              const beneficiaries = beneficiaryData.beneficiaries || [];
              const firstBeneficiary = beneficiaries.find((b: any) => b.selected) || beneficiaries[0];

              // Extract contract number from stepData
              const contractData = stepData['01-01-CONTRAT'] || {};
              const contractNumber = contractData.contractDetails?.number || claimData.contractNumber || '';

              return {
                id: draft.eventId || '',
                eventNumber: draft.eventNumber || '',
                title: `${claimType} Claim - ${draft.eventNumber || (draft.eventId ? draft.eventId.slice(0, 8) : 'Unknown')}`,
                createdAt: draft.createdAt ? new Date(draft.createdAt) : new Date(),
                lastModified: draft.updatedAt ? new Date(draft.updatedAt) : new Date(),
                claimType,
                totalAmount: draft.totalAmount || 0,
                completedScreens: Object.keys(workflow.stepData || {}).length,
                currentScreen: workflow.currentScreenCode || '',
                progressPercentage: draft.progressPercentage || 0,
                status: draft.status,
                contractNumber,
                beneficiaryFirstName: firstBeneficiary?.firstName || '',
                beneficiaryLastName: firstBeneficiary?.lastName || '',
              };
            });

            set({
              draftsList: uiDrafts,
              isLoading: false,
              error: null
            }, false, 'fetchDrafts:success');

          } catch (error: any) {
            logger.error('Failed to fetch drafts', {
              errorType: 'FetchDraftsError',
              store: 'ClaimStore'
            }, error);
            set({
              draftsList: [],
              isLoading: false,
              error: error.message || 'Failed to load drafts'
            }, false, 'fetchDrafts:error');
          }
        },

        // Fetch unified claim list for Home page (Brouillons + Envoyées in a single call).
        // DEV-7027: the BFF returns a flat per-item shape (HomeClaimItem) — 16 fields
        // matching the spec verbatim. claimData/workflow/stepData is never populated
        // on the list response; we only carry what the Home page needs.
        fetchHomeClaims: async () => {
          set({ isLoading: true }, false, 'fetchHomeClaims:start');

          try {
            const response = await getClaimList();

            const normalizeClaimType = (raw: string | null | undefined): string | null => {
              if (!raw) return null;
              const lower = raw.toLowerCase();
              const valid = ['medical', 'cancellation', 'baggage'];
              return valid.includes(lower) ? lower : null;
            };

            // Transform drafts to UI format
            const uiDrafts = response.drafts.filter(draft => draft != null).map((draft) => ({
              id: draft.eventId || '',
              eventNumber: draft.eventNumber || '',
              submissionId: draft.submissionId || null,
              createdAt: draft.createdDate ? new Date(draft.createdDate) : new Date(),
              lastModified: draft.lastModifiedDate ? new Date(draft.lastModifiedDate) : new Date(),
              claimType: normalizeClaimType(draft.claimType),
              totalAmount: draft.totalAmountCAD ?? 0,
              currentScreen: '',
              progressPercentage: draft.progressPercentage || 0,
              status: draft.status,
              contractNumber: draft.contractNumber || '',
              beneficiaryFirstName: draft.beneficiaryFirstName || '',
              beneficiaryLastName: draft.beneficiaryLastName || '',
              incidentDate: draft.eventDate || null,
            }));

            // Transform sent claims to flat UI format
            const uiSent = Object.entries(response.sentByYear || {})
              .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
              .flatMap(([, yearClaims]) => yearClaims)
              .map((claim) => ({
                id: claim.eventId || '',
                eventNumber: claim.eventNumber || '',
                submissionId: claim.submissionId || null,
                createdAt: claim.createdDate ? new Date(claim.createdDate) : new Date(),
                lastModified: claim.lastModifiedDate ? new Date(claim.lastModifiedDate) : new Date(),
                claimType: normalizeClaimType(claim.claimType),
                totalAmount: claim.totalAmountCAD ?? 0,
                status: claim.status,
                contractNumber: claim.contractNumber || '',
                beneficiaryFirstName: claim.beneficiaryFirstName || '',
                beneficiaryLastName: claim.beneficiaryLastName || '',
                incidentDate: claim.eventDate || null,
              }));

            set({
              draftsList: uiDrafts,
              sentClaimsList: uiSent,
              isLoading: false,
              error: null
            }, false, 'fetchHomeClaims:success');

          } catch (error: any) {
            logger.error('Failed to fetch home claims', {
              errorType: 'FetchHomeClaimsError',
              store: 'ClaimStore'
            }, error);
            set({
              draftsList: [],
              sentClaimsList: [],
              isLoading: false,
              error: error.message || 'Failed to load claims'
            }, false, 'fetchHomeClaims:error');
          }
        },

        // Get sent claims (synchronous - returns from state)
        getSentClaims: () => {
          return get().sentClaimsList;
        },

        // Delete a draft
        deleteDraft: (draftId: string) => {
          set(state => {
            const { [draftId]: deleted, ...remainingDrafts } = state.drafts;
            return { drafts: remainingDrafts };
          }, false, 'deleteDraft');
        },

        // Initialize claim with embedded context data
        initializeWithEmbeddedContext: (parentContext: any) => {
          const contextData: any = {};
          
          // Pre-fill contract information
          if (parentContext.contractInfo?.length > 0) {
            contextData.contractInfo = parentContext.contractInfo[0];
            contextData.contractNumber = parentContext.contractInfo[0].contractNumber;
          }
          
          // Pre-fill user profile and beneficiaries
          if (parentContext.userProfile) {
            contextData.userProfile = parentContext.userProfile;
            contextData.beneficiaries = parentContext.userProfile.beneficiaries || [];
          }
          
          // Pre-fill travel information
          if (parentContext.tripInfo?.length > 0) {
            contextData.travelInfo = parentContext.tripInfo[0];
          }
          
          // Update step data with context
          set(state => ({
            stepData: {
              ...state.stepData,
              // Pre-fill contract selection (01-01-CONTRAT)
              '01-01-CONTRAT': { ...state.stepData['01-01-CONTRAT'], ...contextData.contractInfo && { selectedContract: contextData.contractInfo } },
              // Pre-fill beneficiary selection (02M-01-BENEFICIAIRE)
              '02M-01-BENEFICIAIRE': { ...state.stepData['02M-01-BENEFICIAIRE'], ...contextData.beneficiaries && { beneficiaries: contextData.beneficiaries } },
              // Pre-fill travel confirmation (02M-02-VOYAGE)
              '02M-02-VOYAGE': { ...state.stepData['02M-02-VOYAGE'], ...contextData.travelInfo && { travelInfo: contextData.travelInfo } }
            }
          }), false, 'initializeWithEmbeddedContext');
        },

        // Update with cached parent data
        updateWithCachedData: (cacheData: any) => {
          set(state => ({
            stepData: {
              ...state.stepData,
              ...cacheData
            }
          }), false, 'updateWithCachedData');
        },

        // Set current screen code
        setCurrentScreenCode: (screenCode: string) => {
          set({ currentScreenCode: screenCode }, false, 'setCurrentScreenCode');
        },

        // Set deferred claim creation flag
        setDeferClaimCreation: (defer: boolean) => {
          set({ deferClaimCreation: defer }, false, 'setDeferClaimCreation');
        },

        // Get current claim type
        getCurrentClaimType: (): 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' | null => {
          const { stepData } = get();
          const claimTypeScreenData = stepData['02-00-GARANTIE'];

          if (!claimTypeScreenData) return null;

          // Check for selectedType (singular) - what ClaimTypeScreen actually saves
          const selectedType = claimTypeScreenData.selectedType;
          if (selectedType) {
            const upperType = selectedType.toUpperCase();
            if (upperType === 'MEDICAL') return 'MEDICAL';
            if (upperType === 'CANCELLATION') return 'CANCELLATION';
            if (upperType === 'BAGGAGE' || upperType === 'LUGGAGE') return 'BAGGAGE';
          }

          // Fallback: Check for selectedTypes (plural, array) for backward compatibility
          const selectedTypes = claimTypeScreenData.selectedTypes;
          if (selectedTypes && Array.isArray(selectedTypes)) {
            if (selectedTypes.includes('MEDICAL')) return 'MEDICAL';
            if (selectedTypes.includes('CANCELLATION')) return 'CANCELLATION';
            if (selectedTypes.includes('BAGGAGE')) return 'BAGGAGE';
          }

          return null;
        },

        // Initialize parent context data (from embedded app) - separate from stepData
        initializeFromParentContext: (context: { contracts?: any[]; clientId?: string; clientInfo?: any }) => {
          set({
            parentContext: context
          }, false, 'initializeFromParentContext');
        },

        // Get parent context data
        getParentContext: () => {
          return get().parentContext;
        },

        // Get completed screen codes (calculated from stepData keys)
        getCompletedScreenCodes: () => {
          return Object.keys(get().stepData);
        },

        // Set existing event number for linking during claim creation
        setExistingEventNumber: (eventNumber: string | null) => {
          set({ existingEventNumber: eventNumber });
        },

        // Clear any prior claim, stash the selected existing event, and defer
        // creation until the user passes consent (00-02-AVIS). One atomic update.
        beginNewClaimOnExistingEvent: (eventNumber: string) => {
          set(
            { ...initialState, existingEventNumber: eventNumber, deferClaimCreation: true },
            false,
            'beginNewClaimOnExistingEvent'
          );
        },

        // Create a new claim linked to an existing event after the user accepts consent.
        // Reads the stashed existingEventNumber, POSTs to the dedicated BFF endpoint (no contract
        // number — the event carries its own), then hydrates the store from the response (mirrors
        // loadDraft's ingest of a GET) and clears the defer flag and stash.
        // Returns false (and keeps the stash) on failure so the user can retry from the consent screen.
        // NOTE: always creates a MEDICAL claim. The BFF import path forces MEDICAL via
        // mapForExistingEventImport; non-medical existing-event import isn't supported yet.
        createNewClaimOnExistingEvent: async (): Promise<boolean> => {
          const { existingEventNumber } = get();
          if (!existingEventNumber) {
            logger.error('createNewClaimOnExistingEvent called without a stashed existing-event selection', {
              errorType: 'ExistingEventClaimError', store: 'ClaimStore',
            });
            return false;
          }

          set({ isLoading: true }, false, 'createNewClaimOnExistingEvent:start');
          try {
            const response = await createClaimOnExistingEvent(existingEventNumber);

            // Reconstruct the claim from the create response (same shape loadDraft ingests).
            const claimData: any = response.claimData || {};
            const workflow = claimData.workflow || {};
            const currentScreenCode =
              response.currentScreenCode || workflow.currentScreenCode || null;
            // DEV-8026: the legacy in-flight flag dies here, on the first resume, so nothing
            // downstream can spread it back into the claim data.
            const migratedStepData = withoutEligibilityLoading(autoMigrateStepData(workflow.stepData || {}));

            const claim: Claim = {
              id: response.eventId,
              contractNumber: claimData.contractNumber || response.contractNumber || '',
              // DEV-7699: no policyholderID rehydration — see loadDraft
              status: response.status as any,
              totalAmountCAD: claimData.totalAmountCAD || 0,
              rtaEligible: claimData.rtaEligible || false,
              expiresAt: claimData.expiresAt ? new Date(claimData.expiresAt) : new Date(),
              version: claimData.version || 1,
              beneficiaries: claimData.beneficiaries || [],
              expenses: claimData.expenses || [],
              documents: claimData.documents || [],
              medicalEvents: claimData.medicalEvents || [],
              workflow: {
                currentScreenCode,
                stepData: migratedStepData,
                ...(workflow._salesforceMetadata && {
                  _salesforceMetadata: workflow._salesforceMetadata
                }),
              },
              createdAt: response.createdAt ? new Date(response.createdAt) : new Date(),
              updatedAt: response.updatedAt ? new Date(response.updatedAt) : new Date(),
            };

            set({
              currentClaim: claim,
              eventId: response.eventId,
              eventNumber: response.eventNumber || null,
              submissionId: response.submissionId || null,
              referenceNumber: response.referenceNumber || claimData.referenceNumber || null,
              stepData: migratedStepData,
              currentScreenCode,
              referenceClaim: null,
              deferClaimCreation: false,
              existingEventNumber: null,
              isLoading: false,
              error: null,
            }, false, 'createNewClaimOnExistingEvent:success');
            return true;
          } catch (error: any) {
            logger.error('Failed to create claim from existing event', {
              errorType: 'ExistingEventClaimError', store: 'ClaimStore',
            }, error);
            set({ isLoading: false, error: error?.message || 'Failed to create claim from existing event' },
              false, 'createNewClaimOnExistingEvent:error');
            return false;
          }
        },

        /**
         * Load a submitted claim in read-only mode. Generic slice for flows that act on an
         * existing submitted claim (e.g., "append expenses"). Does NOT touch currentClaim, but
         * DOES clear the wizard stepData keys that the append flow writes to — otherwise the
         * wizard would inherit data from whatever the user had previously loaded (e.g. the
         * reference claim itself, loaded into stepData by ClaimDetailsScreen).
         */
        loadReferenceClaim: async (eventId: string, submissionId?: string) => {
          const { getClaim } = await import('../services/api/endpoints/claimEndpoints');
          const response: any = await getClaim(eventId, submissionId);
          const rawClaimData = response.claimData ?? {};
          const workflow = rawClaimData.workflow ?? {};
          const stepData = workflow.stepData ?? {};

          // Clear the wizard stepData keys the append-expenses + append-document flows write
          // to so the new wizard starts with a blank state. Keep everything else untouched.
          const APPEND_WIZARD_KEYS = [
            '06-01-NOUVEAUFRAIS',       // append-expenses intro (clientMessage)
            '06-00-NOUVEAUDOCUMENT',    // append-document intro (clientMessage)
            '04M-00-REMBOURSEMENT',
            '04-01-FACTURES',
            '04-02-VALIDATION',
            '04-03-JUSTIFICATIFS'
          ];
          const currentStepData = get().stepData;
          const clearedStepData = { ...currentStepData };
          APPEND_WIZARD_KEYS.forEach(key => {
            delete clearedStepData[key];
          });
          set({ stepData: clearedStepData }, false, 'loadReferenceClaim:clearWizard');

          const validationSection = stepData['04-02-VALIDATION'] ?? {};
          const previousExpenses: any[] = Array.isArray(validationSection.expenses)
            ? validationSection.expenses
            : [];
          const refundReceivedAmount: number =
            typeof validationSection.refundReceivedAmount === 'number'
              ? validationSection.refundReceivedAmount
              : 0;

          // Travel data lives under a different screen code per flow:
          //   Medical → 02M-02-VOYAGE (departureDate / returnDate)
          //   Cancellation → 02A-02-VOYAGE (plannedDepartureDate / plannedReturnDate, plus actualReturnDate)
          //   Baggage → 02B-02-VOYAGE (departureDate / returnDate)
          // Until DEV-6939 normalises NovaToPortalMapper to write the per-flow screen code,
          // a Nova-imported cancellation may still arrive under 02M-02-VOYAGE — so fall back across all three.
          // TODO: once DEV-6939 ships and the BFF writes to the correct per-flow screen code, replace
          // these fallbacks by an explicit lookup driven by the claim flowType (referenceClaim.flowType
          // or response.flowType) — picking the matching screen code + key set is more robust than
          // probing a fallback chain that can mask real data-shape regressions.
          const travelSection =
            stepData['02M-02-VOYAGE'] ??
            stepData['02A-02-VOYAGE'] ??
            stepData['02B-02-VOYAGE'] ??
            {};
          const departureDate: string | null =
            travelSection.departureDate ?? travelSection.plannedDepartureDate ?? null;
          const returnDate: string | null =
            travelSection.returnDate
            ?? travelSection.plannedReturnDate
            ?? travelSection.actualReturnDate
            ?? null;

          const beneficiarySection = stepData['02M-01-BENEFICIAIRE'] ?? {};
          const beneficiaries: any[] = Array.isArray(beneficiarySection.beneficiaries)
            ? beneficiarySection.beneficiaries
            : [];
          const firstBeneficiary = beneficiaries.find((b: any) => b.selected) || beneficiaries[0] || null;
          const beneficiaryFullName: string | null = firstBeneficiary
            ? [firstBeneficiary.firstName, firstBeneficiary.lastName].filter(Boolean).join(' ') || null
            : null;

          const referenceClaim = {
            eventId: response.eventId ?? eventId,
            eventNumber: response.eventNumber ?? null,
            submissionId: response.submissionId ?? submissionId ?? null,
            flowType: response.flowType ?? rawClaimData.flowType ?? null,
            status: response.status ?? null,
            beneficiaryFullName,
            departureDate,
            returnDate,
            previousExpenses,
            refundReceivedAmount,
            contractNumber: response.contractNumber ?? rawClaimData.contractNumber ?? null,
            rawClaimData
          };

          set({
            referenceClaim,
            eventId: referenceClaim.eventId,
            eventNumber: referenceClaim.eventNumber,
            referenceNumber: Date.now(),
          }, false, 'loadReferenceClaim:success');
          return referenceClaim;
        },

        clearReferenceClaim: () => {
          set({ referenceClaim: null }, false, 'clearReferenceClaim');
        }
      }),
      {
        name: 'claim-store',
        partialize: (state) => ({
          drafts: state.drafts,
          // Don't persist sensitive data like currentClaim in production
        }),
        version: 1
      }
    ),
    { name: 'claim-store' }
  )
);